import { Link } from 'react-router';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface BreadcrumbItem {
    label: string;
    href?: string;
}

interface BreadcrumbProps {
    items: BreadcrumbItem[];
    className?: string;
}

/**
 * Accessible breadcrumb navigation component.
 * Follows WAI-ARIA breadcrumb pattern for screen readers.
 */
export const Breadcrumb = ({ items, className }: BreadcrumbProps) => {
    if (!items || items.length === 0) return null;

    // Add home as first item if not already present
    const allItems: BreadcrumbItem[] = [
        { label: 'Home', href: '/' },
        ...items,
    ];

    return (
        <nav
            aria-label="Breadcrumb navigation"
            className={cn("py-3", className)}
        >
            <ol
                className="flex flex-wrap items-center gap-1 text-sm"
                itemScope
                itemType="https://schema.org/BreadcrumbList"
            >
                {allItems.map((item, index) => {
                    const isLast = index === allItems.length - 1;
                    const isFirst = index === 0;

                    return (
                        <li
                            key={index}
                            className="flex items-center gap-1"
                            itemProp="itemListElement"
                            itemScope
                            itemType="https://schema.org/ListItem"
                        >
                            {/* Separator */}
                            {index > 0 && (
                                <ChevronRight
                                    className="h-4 w-4 text-muted-foreground/70 flex-shrink-0"
                                    aria-hidden="true"
                                />
                            )}

                            {/* Link or Current Page */}
                            {isLast ? (
                                <span
                                    className="text-muted-foreground font-medium truncate max-w-[200px] sm:max-w-none"
                                    aria-current="page"
                                    itemProp="name"
                                >
                                    {item.label}
                                </span>
                            ) : (
                                <Link
                                    to={item.href || '/'}
                                    className={cn(
                                        "text-muted-foreground hover:text-primary transition-colors",
                                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded",
                                        "flex items-center gap-1 truncate max-w-[150px] sm:max-w-none"
                                    )}
                                    itemProp="item"
                                >
                                    {isFirst && (
                                        <Home className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                                    )}
                                    <span itemProp="name" className={isFirst ? 'sr-only sm:not-sr-only' : ''}>
                                        {item.label}
                                    </span>
                                </Link>
                            )}
                            <meta itemProp="position" content={String(index + 1)} />
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};
